// The character-name rule, once. The server enforces it on POST /v1/characters (and stays the
// authority: it also checks the name is free); the create screen shows the same rules up front and
// checks them as the player types.

export const CHARACTER_NAME_MIN = 3;
export const CHARACTER_NAME_MAX = 16;

export type CharacterNameRule = "length" | "start" | "chars";

/** Why a name fails: the rule it breaks and the message the player sees. */
export interface CharacterNameProblem {
  rule: CharacterNameRule;
  message: string;
}

/** The rules as the create screen lists them, in the order they are checked. */
export const CHARACTER_NAME_RULES: { rule: CharacterNameRule; text: string }[] = [
  { rule: "length", text: `${CHARACTER_NAME_MIN} to ${CHARACTER_NAME_MAX} characters` },
  { rule: "start", text: "Starts with a letter" },
  { rule: "chars", text: "Letters and apostrophes only (no spaces or digits)" },
];

/**
 * The name as typed, made ready to check and store: trimmed, and typographic apostrophes (iOS
 * Smart Punctuation turns ' into ’) folded to a plain '. Client and server both run it first.
 */
export function normalizeCharacterName(raw: string): string {
  return raw.trim().replace(/[\u2018\u2019\u02BC]/g, "'");
}

const LETTER = /^[A-Za-z]$/;
const CHARS = /^[A-Za-z']*$/;

/** Every rule the (already normalized) name breaks, in rule order; empty when it is valid. */
export function characterNameProblems(name: string): CharacterNameProblem[] {
  const out: CharacterNameProblem[] = [];
  if (name.length < CHARACTER_NAME_MIN || name.length > CHARACTER_NAME_MAX)
    out.push({
      rule: "length",
      message: `Name must be ${CHARACTER_NAME_MIN} to ${CHARACTER_NAME_MAX} characters.`,
    });
  if (!LETTER.test(name.slice(0, 1)))
    out.push({ rule: "start", message: "Name must start with a letter." });
  if (!CHARS.test(name))
    out.push({ rule: "chars", message: "Name may only use letters and apostrophes." });
  return out;
}

/** The first rule the name breaks, or null when it is valid. */
export function characterNameProblem(name: string): CharacterNameProblem | null {
  return characterNameProblems(name)[0] ?? null;
}
